export const PLANNER_ANALYTICS_EVENTS=Object.freeze([
  'planner_opened',
  'target_time_set',
  'plan_generated',
  'plan_applied',
  'buffer_changed',
  'schedule_manual_override',
  'compatibility_warning',
  'planner_notification_enabled',
  'planner_notification_interacted',
  'planner_session_started',
  'planner_session_completed'
]);

const ALLOWED=new Set(PLANNER_ANALYTICS_EVENTS);
const COMPATIBILITY=new Set(['compatible','incompatible','review','unknown']);
const BLOCK_KINDS=new Set(['preparation','curing','drying','stage','buffer']);
const IMPACT=new Set(['continuous','gap','overlap','unknown']);
const NOTIFICATION_KINDS=new Set([
  'preparation_start','curing_start','drying_start','prepare_smoker','live_start'
]);
const ACTIONS=new Set(['tap','other']);

function count(value,max=1000){
  const number=Number(value);
  if(!Number.isFinite(number))return 0;
  return Math.max(0,Math.min(max,Math.floor(number)));
}

function minutes(value,max=24*60){
  const number=Number(value);
  if(!Number.isFinite(number))return 0;
  return Math.max(0,Math.min(max,Math.floor(number)));
}

function choice(value,allowed,fallback='unknown'){
  const key=String(value??'').trim().toLowerCase();
  return allowed.has(key)?key:fallback;
}

function runId(value){
  const id=String(value??'').trim();
  if(!/^[a-zA-Z0-9_-]{8,80}$/.test(id))return null;
  return id;
}

function bool(value){return Boolean(value);}

function base(input={}){
  const id=runId(input.plannerRunId);
  return Object.freeze({
    ...(id?{planner_run_id:id}:{}),
    offline:bool(input.offline)
  });
}

function propertiesFor(name,input={}){
  switch(name){
    case 'planner_opened':
      return Object.freeze({
        eligible_session_count:count(input.eligibleSessionCount,100),
        offline:bool(input.offline)
      });
    case 'target_time_set':
      return Object.freeze({
        ...base(input),
        buffer_minutes:minutes(input.bufferMinutes),
        reminders_enabled:bool(input.remindersEnabled),
        recipe_derived:bool(input.recipeDerived)
      });
    case 'plan_generated':
      return Object.freeze({
        ...base(input),
        block_count:count(input.blockCount,200),
        warning_count:count(input.warningCount,100),
        compatibility:choice(input.compatibility,COMPATIBILITY),
        complete:bool(input.complete),
        cross_day:bool(input.crossDay),
        reminders_enabled:bool(input.remindersEnabled)
      });
    case 'plan_applied':
      return Object.freeze({
        ...base(input),
        block_count:count(input.blockCount,200),
        manual_override_count:count(input.manualOverrideCount,200),
        reminders_enabled:bool(input.remindersEnabled),
        compatibility:choice(input.compatibility,COMPATIBILITY)
      });
    case 'buffer_changed':
      return Object.freeze({
        ...base(input),
        previous_minutes:minutes(input.previousMinutes),
        next_minutes:minutes(input.nextMinutes)
      });
    case 'schedule_manual_override':
      return Object.freeze({
        ...base(input),
        block_kind:choice(input.blockKind,BLOCK_KINDS),
        changed_field_count:count(input.changedFieldCount,10),
        downstream_count:count(input.downstreamCount,200),
        impact:choice(input.impact,IMPACT)
      });
    case 'compatibility_warning':
      return Object.freeze({
        ...base(input),
        compatibility:choice(input.compatibility,COMPATIBILITY),
        finding_count:count(input.findingCount,100)
      });
    case 'planner_notification_enabled':
      return Object.freeze({
        ...base(input),
        reminder_count:count(input.reminderCount,200)
      });
    case 'planner_notification_interacted':
      return Object.freeze({
        notification_kind:choice(input.kind,NOTIFICATION_KINDS),
        action:choice(input.action,ACTIONS,'other')
      });
    case 'planner_session_started':
    case 'planner_session_completed':
      return base(input);
    default:
      throw new TypeError('Unsupported Planner analytics event');
  }
}

export function plannerRunIdFromSession(session){
  return runId(session?.plannerScheduleSnapshot?.plannerRunId);
}

export function createPlannerAnalytics({analytics=null}={}){
  function track(name,input={}){
    if(!ALLOWED.has(name))throw new TypeError('Unsupported Planner analytics event');
    const properties=propertiesFor(name,input);
    try{
      const result=analytics?.track?.(name,properties);
      return result??Object.freeze({accepted:false,reason:'ANALYTICS_UNAVAILABLE'});
    }catch{
      return Object.freeze({accepted:false,reason:'ANALYTICS_FAILED'});
    }
  }

  return Object.freeze({
    events:PLANNER_ANALYTICS_EVENTS,
    track,
    opened:input=>track('planner_opened',input),
    targetTimeSet:input=>track('target_time_set',input),
    planGenerated:input=>track('plan_generated',input),
    planApplied:input=>track('plan_applied',input),
    bufferChanged:input=>track('buffer_changed',input),
    manualOverride:input=>track('schedule_manual_override',input),
    compatibilityWarning:input=>track('compatibility_warning',input),
    notificationEnabled:input=>track('planner_notification_enabled',input),
    notificationInteracted:input=>track('planner_notification_interacted',input),
    sessionStarted:input=>track('planner_session_started',input),
    sessionCompleted:input=>track('planner_session_completed',input)
  });
}
